import { esc } from "./toast";

export type UiConfirmOptions = {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

export type UiAlertOptions = {
  title?: string;
  message?: string;
  confirmLabel?: string;
};

const DIALOG_ID = "uiDialog";

function removeExistingDialog(): void {
  document.getElementById(DIALOG_ID)?.remove();
}

function bindDialogDismiss(
  gate: HTMLElement,
  onResult: (value: boolean) => void,
): void {
  const cancelBtn = gate.querySelector<HTMLButtonElement>("#uiDialogCancel");
  const okBtn = gate.querySelector<HTMLButtonElement>("#uiDialogOk");

  function finish(result: boolean): void {
    document.removeEventListener("keydown", onKey);
    gate.remove();
    onResult(result);
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      e.preventDefault();
      finish(false);
    } else if (e.key === "Enter" && document.activeElement !== cancelBtn) {
      e.preventDefault();
      finish(true);
    }
  }

  gate.addEventListener("click", (e) => {
    if (e.target === gate) {
      finish(false);
    }
  });
  cancelBtn?.addEventListener("click", () => finish(false));
  okBtn?.addEventListener("click", () => finish(true));
  document.addEventListener("keydown", onKey);
}

export function uiConfirm({
  title = "确认",
  message = "",
  confirmLabel = "确定",
  cancelLabel = "取消",
  danger = false,
}: UiConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    removeExistingDialog();

    const gate = document.createElement("div");
    gate.id = DIALOG_ID;
    gate.className = "modal-overlay";
    const okClass = danger ? "btn btn-danger" : "btn btn-primary";
    gate.innerHTML = `
      <div class="modal-dialog panel" role="dialog" aria-modal="true" aria-labelledby="uiDialogTitle">
        <h2 class="modal-dialog-title" id="uiDialogTitle">${esc(title)}</h2>
        ${message ? `<p class="modal-dialog-message">${esc(message)}</p>` : ""}
        <div class="modal-dialog-actions">
          <button type="button" class="${okClass}" id="uiDialogOk">${esc(confirmLabel)}</button>
          <button type="button" class="btn" id="uiDialogCancel">${esc(cancelLabel)}</button>
        </div>
      </div>
    `;
    document.body.appendChild(gate);

    const okBtn = gate.querySelector<HTMLButtonElement>("#uiDialogOk");
    requestAnimationFrame(() => okBtn?.focus());

    bindDialogDismiss(gate, resolve);
  });
}

export function uiAlert({
  title = "提示",
  message = "",
  confirmLabel = "确定",
}: UiAlertOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    removeExistingDialog();

    const gate = document.createElement("div");
    gate.id = DIALOG_ID;
    gate.className = "modal-overlay";
    gate.innerHTML = `
      <div class="modal-dialog panel" role="dialog" aria-modal="true" aria-labelledby="uiDialogTitle">
        <h2 class="modal-dialog-title" id="uiDialogTitle">${esc(title)}</h2>
        ${message ? `<p class="modal-dialog-message">${esc(message)}</p>` : ""}
        <div class="modal-dialog-actions">
          <button type="button" class="btn btn-primary" id="uiDialogOk">${esc(confirmLabel)}</button>
        </div>
      </div>
    `;
    document.body.appendChild(gate);

    const okBtn = gate.querySelector<HTMLButtonElement>("#uiDialogOk");

    function finish(): void {
      document.removeEventListener("keydown", onKey);
      gate.remove();
      resolve();
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        finish();
      }
    }

    gate.addEventListener("click", (e) => {
      if (e.target === gate) {
        finish();
      }
    });
    okBtn?.addEventListener("click", finish);
    document.addEventListener("keydown", onKey);

    requestAnimationFrame(() => okBtn?.focus());
  });
}
